import React, { useState } from 'react';
import {
  Box, Grid, Text, Button, Input, Textarea,
  FormControl, FormLabel, Flex,
} from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = motion(Box);

const inputStyles = {
  bg: 'white',
  border: '1px solid',
  borderColor: 'rgba(22,33,58,0.18)',
  borderRadius: '2px',
  color: 'oid.text',
  fontSize: 'sm',
  height: '48px',
  _placeholder: { color: 'rgba(22,33,58,0.35)' },
  _focus: { borderColor: 'oid.accent', boxShadow: 'none', bg: 'white' },
  _hover: { borderColor: 'rgba(22,33,58,0.3)' },
};

const labelStyles = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'rgba(22,33,58,0.5)',
  mb: 2,
};

function Contact() {
  const shouldReduce = useReducedMotion();
  const ease = [0.25, 0.1, 0.25, 1];

  const [form, setForm]       = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitted(true);
  };

  return (
    <Box as="section" id="contact-form" aria-label="Contact us"
      bg="oid.bg" py={{ base: '55px', md: '89px' }}>
      <Grid templateColumns={{ base: '1fr', lg: '38.2% 1fr' }}
        gap={{ base: 12, lg: 20 }} alignItems="start"
        maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

        {/* Left — heading */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease }}
          position={{ lg: 'sticky' }}
          top={{ lg: 'calc(var(--nav-h) + 40px)' }}>
          <Box mb={6}>
            <Box as="span" display="block" width="24px" height="2px"
              bg="oid.accent" mb={4} aria-hidden="true" />
            <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
              textTransform="uppercase" color="oid.accent">
              Contact
            </Text>
          </Box>
          <Text as="h2" fontWeight={700} lineHeight={1.1} color="oid.text"
            sx={{ fontSize: 'clamp(28px, 3.5vw, 48px)' }} mb={6}>
            Get in touch.
          </Text>
          <Text fontSize="sm" fontWeight={400} color="oid.muted"
            lineHeight={1.618} maxWidth="320px" mb={8}>
            Whether you're exploring deployment, discussing a partnership, or
            simply want to learn more about O-ID, we'd love to hear from you.
          </Text>
          <Flex direction="column" gap={3}>
            {[
              { label: 'General enquiries', value: 'hello@o-id.io' },
              { label: 'Partnerships',       value: 'partners@o-id.io' },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Text fontSize="9px" fontWeight={600} letterSpacing="0.28em"
                  textTransform="uppercase" color="rgba(22,33,58,0.4)" mb={1}>
                  {label}
                </Text>
                <Text fontSize="sm" color="oid.muted">{value}</Text>
              </Box>
            ))}
          </Flex>
        </MotionBox>

        {/* Right — form */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.75, delay: 0.1, ease }}>
          {submitted ? (
            <Flex direction="column" gap={5} pt={4}>
              <Box width="32px" height="2px" bg="oid.accent" />
              <Text fontWeight={700} color="oid.text"
                sx={{ fontSize: 'clamp(20px, 2.5vw, 28px)' }}>
                Message received.
              </Text>
              <Text fontSize="sm" color="oid.muted" lineHeight={1.618}>
                Thank you, {form.name}. We'll get back to you at {form.email} shortly.
              </Text>
            </Flex>
          ) : (
            <Box as="form" onSubmit={handleSubmit} display="flex"
              flexDirection="column" gap={6}>
              <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={5}>
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>Name</FormLabel>
                  <Input {...inputStyles} placeholder="Your name"
                    value={form.name} onChange={set('name')} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel {...labelStyles}>Email</FormLabel>
                  <Input {...inputStyles} type="email" placeholder="your@email.com"
                    value={form.email} onChange={set('email')} />
                </FormControl>
              </Grid>

              <FormControl isRequired>
                <FormLabel {...labelStyles}>Message</FormLabel>
                <Textarea
                  {...inputStyles}
                  height="auto"
                  minH="160px"
                  resize="vertical"
                  placeholder="Tell us about your use case, project, or question…"
                  value={form.message}
                  onChange={set('message')}
                />
              </FormControl>

              <Flex align="center" gap={5} flexWrap="wrap">
                <Button type="submit"
                  size="md" px={8} py={5}
                  fontSize="11px" letterSpacing="0.14em" textTransform="uppercase">
                  Send Message
                </Button>
                <Text fontSize="10px" color="rgba(22,33,58,0.35)"
                  letterSpacing="0.06em">
                  We respond within 2 business days.
                </Text>
              </Flex>
            </Box>
          )}
        </MotionBox>
      </Grid>
    </Box>
  );
}

export default Contact;
